import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Building2,
  Check,
  X,
  Loader,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import AuthHeader from '../../components/auth/AuthHeader'
import { vendorService } from "../../services/vendorService";

const SignupPage = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    vendorName: "",
    email: "",
    countryCode: "+91",
    phoneNumber: "",
    emailOtp: "",
    phoneOtp: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
    role: "vendor", // Always vendor for vendor app
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpsId, setOtpsId] = useState(null);
  const [countryCodes, setCountryCodes] = useState([]);
  const [countryCodesLoading, setCountryCodesLoading] = useState(true);

  const { register } = useAuth();

  // Fetch country codes on component mount
  useEffect(() => {
    const fetchCountryCodes = async () => {
      try {
        setCountryCodesLoading(true);
        const response = await vendorService.getEnum('country_codes');
        if (response.success && response.data) {
          const codes = response.data.map(item => item.value);
          setCountryCodes(codes);
        } else {
          setCountryCodes(["+91"]);
        }
      } catch (error) {
        console.error('Failed to fetch country codes:', error);
        setCountryCodes(["+91"]);
      } finally {
        setCountryCodesLoading(false);
      }
    };

    fetchCountryCodes();
  }, []);

  // Comprehensive form validation
  const validateForm = (validateOTP = false) => {
    const newErrors = {};

    if (!validateOTP) {
      // Email validation
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Invalid email format";
      }

      // Phone number validation
      if (!formData.phoneNumber.trim()) {
        newErrors.phoneNumber = "Phone number is required";
      } else if (!/^[6-9]\d{9}$/.test(formData.phoneNumber)) {
        newErrors.phoneNumber = "Invalid phone number format";
      }

      // Company name validation
      if (!formData.vendorName.trim()) {
        newErrors.vendorName = "Company Name is required";
      }

      // Password validation
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      } else if (!/(?=.*[a-z])(?=.*[A-Z])/.test(formData.password)) {
        newErrors.password = "Password must contain uppercase and lowercase letters";
      } else if (!/(?=.*\d)/.test(formData.password)) {
        newErrors.password = "Password must contain at least one number";
      } else if (!/(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/.test(formData.password)) {
        newErrors.password = "Password must contain at least one special character";
      }

      // Confirm password validation
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Please confirm your password";
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }

      // Terms and conditions validation
      if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = "You must agree to the terms and conditions";
      }
    } else {
      // Validate OTP fields
      if (!formData.emailOtp.trim()) {
        newErrors.emailOtp = "Email OTP is required";
      } else if (!/^\d{6}$/.test(formData.emailOtp)) {
        newErrors.emailOtp = "Email OTP must be 6 digits";
      }

      if (!formData.phoneOtp.trim()) {
        newErrors.phoneOtp = "Phone OTP is required";
      } else if (!/^\d{6}$/.test(formData.phoneOtp)) {
        newErrors.phoneOtp = "Phone OTP must be 6 digits";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getPasswordStrength = (password) => {
    if (!password) return 0;
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthColor = (strength) => {
    switch (strength) {
      case 1: return "bg-red-500";
      case 2: return "bg-orange-500";
      case 3: return "bg-yellow-500";
      case 4: return "bg-green-500";
      default: return "bg-gray-200 dark:bg-gray-700";
    }
  };

  const getPasswordStrengthText = (password) => {
    const strength = getPasswordStrength(password);
    switch (strength) {
      case 0: return "Enter a password";
      case 1: return "Very weak";
      case 2: return "Weak";
      case 3: return "Good";
      case 4: return "Strong";
      default: return "";
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otpSent) {
      if (!validateForm(false)) return;

      setIsLoading(true);

      try {
        const response = await vendorService.sendOtp({
          role: "vendor",
          name: formData.vendorName,
          email: formData.email,
          phone: formData.phoneNumber,
          password: formData.password,
        });

        if (response.success) {
          setOtpsId(response.data.otps_id);
          setOtpSent(true);
          toast.success(response.message || "OTP sent successfully!");
        } else {
          toast.error(response.message || "Failed to send OTP.");
        }
      } catch (error) {
        toast.error(error.message || "Something went wrong. Please try again.");
      } finally {
        setIsLoading(false);
      }
    } else {
      if (!validateForm(true)) return;

      setIsLoading(true);

      try {
        const emailVerifyResponse = await vendorService.verifyEmailOtp({
          otps_id: otpsId,
          role: "vendor",
          name: formData.vendorName,
          email: formData.email,
          email_otp: formData.emailOtp,
          phone: formData.phoneNumber,
          password: formData.password,
        });

        if (!emailVerifyResponse.success) {
          toast.error(emailVerifyResponse.message || "Email OTP verification failed.");
          return;
        }

        const phoneVerifyResponse = await vendorService.verifyPhoneOtp({
          otps_id: otpsId,
          role: "vendor",
          name: formData.vendorName,
          email: formData.email,
          phone: formData.phoneNumber,
          phone_otp: formData.phoneOtp,
          password: formData.password,
        });

        if (!phoneVerifyResponse.success) {
          toast.error(phoneVerifyResponse.message || "Phone OTP verification failed.");
          return;
        }

        const registerResponse = await vendorService.register({
          otps_id: otpsId,
          role: "vendor",
          name: formData.vendorName,
          email: formData.email,
          phone: formData.phoneNumber,
          password: formData.password,
        });

        if (registerResponse.success || registerResponse.token) {
          const token = registerResponse.token || registerResponse.data?.token || registerResponse.data?.access_token;
          
          const registrationCredentials = {
            email: formData.email,
            password: formData.password,
            token: token,
            role: "vendor"
          };
          
          navigate("/auth/login", {
            state: {
              autoLogin: true,
              credentials: registrationCredentials,
              message: registerResponse.message || "Registration successful! Logging you in..."
            }
          });
        } else {
          toast.error(registerResponse.message || "Registration failed.");
        }
      } catch (error) {
        toast.error(error.message || "Something went wrong. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900">
      <AuthHeader />

      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] p-4 py-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card w-full max-w-lg p-5 md:p-6 shadow-glow"
        >
          {/* Header */}
          <div className="text-center mb-5">
            <h1 className="text-2xl font-display font-bold text-secondary-900 dark:text-secondary-100 mb-1.5 tracking-tight">
              Vendor Registration
            </h1>
            <p className="text-xs text-secondary-600 dark:text-secondary-400 max-w-sm mx-auto">
              Create your vendor account to manage bench resources and find projects
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3.5">
            {/* Company Name */}
            <div className="space-y-1">
              <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                Company Name <span className="text-error-500">*</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 w-3.5 h-3.5 z-10" />
                <input
                  type="text"
                  value={formData.vendorName}
                  onChange={(e) => handleInputChange("vendorName", e.target.value)}
                  className="input pl-9 h-9 text-sm"
                  placeholder="Enter your company name"
                />
              </div>
              {errors.vendorName && (
                <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                  <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                  {errors.vendorName}
                </p>
              )}
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                  Email Address <span className="text-error-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 w-3.5 h-3.5 z-10" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    disabled={otpSent}
                    className={`input pl-9 h-9 text-sm ${
                      otpSent ? "bg-secondary-100 dark:bg-secondary-800/50 cursor-not-allowed opacity-70" : ""
                    }`}
                    placeholder="Enter your email"
                  />
                </div>
                {errors.email && (
                  <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                    <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                  Phone Number <span className="text-error-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.countryCode}
                    onChange={(e) => handleInputChange("countryCode", e.target.value)}
                    disabled={otpSent || countryCodesLoading}
                    className={`input w-12 h-9 text-xs appearance-none cursor-pointer ${
                      otpSent || countryCodesLoading ? "bg-secondary-100 dark:bg-secondary-800/50 cursor-not-allowed opacity-70" : ""
                    }`}
                  >
                    {countryCodesLoading ? (
                      <option>+91</option>
                    ) : countryCodes.length === 0 ? (
                      <option value="+91">+91</option>
                    ) : (
                      countryCodes.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="relative flex-1">
                    <Phone className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 w-3.5 h-3.5 z-10" />
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                      disabled={otpSent}
                      className={`input pl-9 h-9 text-sm ${
                        otpSent ? "bg-secondary-100 dark:bg-secondary-800/50 cursor-not-allowed opacity-70" : ""
                      }`}
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>
                {errors.phoneNumber && (
                  <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                    <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                    {errors.phoneNumber}
                  </p>
                )}
              </div>
            </div>

            {/* Password & Confirm Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                  Password <span className="text-error-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 w-3.5 h-3.5 z-10" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    className="input pl-9 pr-9 h-9 text-sm"
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {formData.password && (
                  <div className="mt-1.5">
                    <div className="flex items-center gap-1 mb-0.5">
                      {[1, 2, 3, 4].map((level) => (
                        <div
                          key={level}
                          className={`h-0.5 flex-1 rounded-full transition-all ${
                            getPasswordStrength(formData.password) >= level
                              ? getPasswordStrengthColor(getPasswordStrength(formData.password))
                              : "bg-secondary-200 dark:bg-secondary-700"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400">
                      {getPasswordStrengthText(formData.password)}
                    </p>
                  </div>
                )}
                {errors.password && (
                  <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                    <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                    {errors.password}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                  Confirm Password <span className="text-error-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 w-3.5 h-3.5 z-10" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                    className={`input pl-9 pr-9 h-9 text-sm ${
                      formData.confirmPassword && formData.password !== formData.confirmPassword
                        ? "border-error-500 dark:border-error-500"
                        : formData.confirmPassword && formData.password === formData.confirmPassword
                        ? "border-success-500 dark:border-success-500"
                        : ""
                    }`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  {formData.confirmPassword && (
                    <div className="absolute right-9 top-1/2 transform -translate-y-1/2">
                      {formData.password === formData.confirmPassword ? (
                        <Check className="w-3.5 h-3.5 text-success-500" />
                      ) : (
                        <X className="w-3.5 h-3.5 text-error-500" />
                      )}
                    </div>
                  )}
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-error-500 text-xs mt-0.5">Passwords do not match</p>
                )}
                {errors.confirmPassword && (
                  <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                    <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            {/* Email OTP & Phone OTP */}
            {otpSent && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                    Email OTP <span className="text-error-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 w-3.5 h-3.5 z-10" />
                    <input
                      type="text"
                      value={formData.emailOtp}
                      onChange={(e) => handleInputChange("emailOtp", e.target.value.replace(/\D/g, "").slice(0, 6))}
                      maxLength="6"
                      className="input pl-9 h-10 tracking-[0.25em] text-center text-sm font-semibold"
                      placeholder="000000"
                    />
                  </div>
                  {errors.emailOtp && (
                    <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                      <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                      {errors.emailOtp}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                    Phone OTP <span className="text-error-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 w-3.5 h-3.5 z-10" />
                    <input
                      type="text"
                      value={formData.phoneOtp}
                      onChange={(e) => handleInputChange("phoneOtp", e.target.value.replace(/\D/g, "").slice(0, 6))}
                      maxLength="6"
                      className="input pl-9 h-10 tracking-[0.25em] text-center text-sm font-semibold"
                      placeholder="000000"
                    />
                  </div>
                  {errors.phoneOtp && (
                    <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                      <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                      {errors.phoneOtp}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Terms & Conditions */}
            <div className="flex items-start gap-2 pt-0.5">
              <input
                type="checkbox"
                id="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={(e) => handleInputChange("agreeToTerms", e.target.checked)}
                className="w-3.5 h-3.5 mt-0.5 border-2 border-secondary-300 dark:border-secondary-600 rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 bg-white dark:bg-secondary-800 text-primary-600 cursor-pointer"
              />
              <label htmlFor="agreeToTerms" className="text-xs text-secondary-600 dark:text-secondary-400 leading-relaxed cursor-pointer">
                I agree to the{" "}
                <a href="#" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold underline-offset-2 hover:underline transition-colors">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="#" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold underline-offset-2 hover:underline transition-colors">
                  Privacy Policy
                </a>
              </label>
            </div>
            {errors.agreeToTerms && (
              <p className="text-error-500 text-xs flex items-center gap-1">
                <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                {errors.agreeToTerms}
              </p>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full h-10 text-xs font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            >
              {isLoading ? (
                <>
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                  <span>{otpSent ? "Verifying..." : "Sending OTP..."}</span>
                </>
              ) : (
                <>
                  <span>
                    {!otpSent
                      ? formData.email && formData.phoneNumber
                        ? "Send OTP"
                        : "Create Vendor Account"
                      : "Submit OTP"}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>

            {/* Back/Resend OTP Button */}
            {otpSent && (
              <div className="grid grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={() => setOtpSent(false)}
                  className="btn-outline h-9 text-xs font-semibold"
                >
                  Change Email/Phone
                </button>
                <button
                  type="button"
                  disabled
                  className="btn-secondary h-9 text-xs font-semibold opacity-50 cursor-not-allowed"
                >
                  Resend OTP
                </button>
              </div>
            )}
          </form>

          {/* Sign In Link */}
          <div className="mt-4 pt-3 border-t border-secondary-200 dark:border-secondary-700 text-center">
            <p className="text-xs text-secondary-600 dark:text-secondary-400">
              Already have an account?{" "}
              <Link
                to="/auth/login"
                className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold underline-offset-2 hover:underline transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SignupPage;
